import { z } from 'zod'

/**
 * Voice: the device streams audio straight to a speech service with a short-lived token from
 * hugo, and only the transcript comes back into hugo (decision 5). These are the contracts hugo
 * and its clients share; the protocol to the service lives in packages/ai.
 */

/** The languages hugo understands when spoken (and parses when typed). */
export const speechLanguages = ['en', 'de'] as const
export const speechLanguageSchema = z.enum(speechLanguages)
export type SpeechLanguage = z.output<typeof speechLanguageSchema>

/**
 * `off`: no voice. `fake`: the test stack's fake speech service (scripted transcripts, test mode
 * or outside production only). A real service comes with its own mode once one is chosen.
 */
export const speechModes = ['off', 'fake'] as const
export const speechModeSchema = z.enum(speechModes)
export type SpeechMode = z.output<typeof speechModeSchema>

/** Audio as the speech service takes it: 16 kHz mono, 16-bit little-endian PCM. */
export const SPEECH_SAMPLE_RATE = 16_000

/** The most a client may stream in one session; the service stops there. */
export const SPEECH_SESSION_MAX_MS = 60_000

/**
 * A speech session a client may open: where, with which short-lived token (a secret: never
 * logged or stored), in which audio format and language.
 */
export const speechSessionSchema = z.object({
  provider: z.literal('fake'),
  url: z.url({ protocol: /^wss?$/ }),
  token: z.string().min(1).max(2000),
  /** The token opens a session until then. */
  expiresAt: z.iso.datetime(),
  sampleRate: z.literal(SPEECH_SAMPLE_RATE),
  encoding: z.literal('pcm16'),
  language: speechLanguageSchema,
})
export type SpeechSession = z.output<typeof speechSessionSchema>

/** Transcripts are at most this long (a dictated item, not a speech). */
export const TRANSCRIPT_MAX = 1000
